import { useCallback, useEffect, useRef } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Pieces the pages share with the shell.
 *
 * These live here rather than in ModernistSite.js because ModernistSite imports
 * every page and every page needs useReveal — putting them in the same file
 * makes the graph circular, which happens to work today only because nothing
 * reads the bindings at module-evaluation time. Not worth relying on.
 */

/**
 * Scroll reveal.
 *
 * The design assigns inline opacity/transform to every [data-reveal] and drives
 * them with one IntersectionObserver. Same idea, but the transition lives in
 * CSS (.m-reveal / .is-in) so it can be disabled by prefers-reduced-motion
 * without JavaScript having to know about it.
 */
export const useReveal = (deps = []) => {
  useEffect(() => {
    let nodes = Array.from(document.querySelectorAll('.m-reveal:not(.is-in)'));
    if (nodes.length === 0) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => n.classList.add('is-in'));
      return undefined;
    }

    /**
     * Anything already on screen is revealed straight away, by geometry.
     *
     * Observer callbacks are delivered with the rendering steps, so a page that
     * never gets a frame — opened in a background tab, prerendered — would
     * leave its first screenful sitting at opacity 0. This also removes the
     * one-frame pop-in that above-the-fold content had on every load.
     */
    const onScreen = (el) => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    };

    nodes.forEach((n, i) => {
      if (!onScreen(n)) return;
      n.style.transitionDelay = `${(i % 6) * 0.06}s`;
      n.classList.add('is-in');
    });
    nodes = nodes.filter((n) => !n.classList.contains('is-in'));
    if (nodes.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          // Stagger within a row, as the design does, without a timer per node.
          const index = nodes.indexOf(entry.target);
          entry.target.style.transitionDelay = `${(index % 6) * 0.06}s`;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15 }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
    // `deps` is the caller's array by design — the pages pass what should
    // re-run the sweep (e.g. the active project filter).
  }, deps);
};

/**
 * Running band. The duplicated span is the seam-free loop — the second copy
 * scrolls in exactly as the first leaves, so the track never shows a gap.
 */
export const Marquee = () => (
  <div className="m-marquee" aria-hidden="true">
    <div className="m-marquee__track">
      <span>Code — Create — Innovate — Machine Learning — Web — Android — Code — Create — Innovate — Machine Learning — Web — Android —</span>
      <span>Code — Create — Innovate — Machine Learning — Web — Android — Code — Create — Innovate — Machine Learning — Web — Android —</span>
    </div>
  </div>
);

/**
 * Pointer tilt, as the design does it: read the cursor position within the card
 * and rotate on both axes.
 *
 * Written straight to style.transform rather than through state — this fires on
 * every mousemove and a re-render per event would be the most expensive thing
 * on the page. Skipped entirely on coarse pointers and reduced motion.
 */
export const useTilt = () => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || still) return undefined;

    /**
     * The scroll reveal leaves a 0.6s transform transition on the card, which
     * would make the tilt drift half a second behind the cursor. An inline
     * transition outranks it — and has to restate box-shadow, since setting the
     * shorthand replaces the whole declaration.
     */
    const onEnter = () => {
      node.style.transition = 'transform 0.12s ease-out, box-shadow 0.25s ease';
    };

    const onMove = (event) => {
      const r = node.getBoundingClientRect();
      const px = (event.clientX - r.left) / r.width - 0.5;
      const py = (event.clientY - r.top) / r.height - 0.5;
      node.style.transform =
        `perspective(1400px) rotateY(${(px * 12).toFixed(2)}deg) ` +
        `rotateX(${(-py * 12).toFixed(2)}deg) translateZ(18px)`;
    };

    const onLeave = () => {
      node.style.transform = '';
    };

    node.addEventListener('pointerenter', onEnter);
    node.addEventListener('pointermove', onMove);
    node.addEventListener('pointerleave', onLeave);
    return () => {
      node.removeEventListener('pointerenter', onEnter);
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return ref;
};

/** Navigate without leaving the Modernist shell. */
export const useGo = () => {
  const navigate = useNavigate();
  return useCallback((path) => navigate(path), [navigate]);
};
